/*
 * Does a row have a value of its own?
 *
 * The grid used to answer this with "does it have children" — a row with
 * children was a folder, and a folder shows nothing in the value column, has no
 * freshness, and cannot be written. For a PLC symbol tree that is exactly right:
 * `DB10.Motor1` is a path prefix, not a thing with a reading.
 *
 * A BROKER TOPIC breaks the equivalence. The integrations service publishes a row
 * for the topic itself carrying the verbatim message (`topic/flatten.go`,
 * `wholeTopicItem` — kind "topic", data type "JSON"), alongside one row per
 * flattened field. The broker really is carrying a message under exactly that
 * name. Whether that row also has CHILDREN depends on which characters its field
 * names happen to contain, which is no basis for deciding whether it has a value.
 *
 * So the two questions are separated here: `logicalType` keeps a value-bearing
 * parent's real type instead of flattening it to "object", and `carriesValue`
 * answers the value question from that type rather than from the child count.
 *
 * Pure on purpose — no DOM, no i18n runtime — following `valueAge.ts`: this is
 * where the rules live and where they are tested; the grid is the thin caller.
 */

/**
 * Node kinds that carry a value of their own even when they have children.
 *
 * MIRRORS THE SERVER. `valueBearingKinds` in
 * `services/api-gateway/internal/bridge/symbol_index.go` decides which rows get a
 * live-value LEASE. If these two sets disagree, the failure is silent and
 * asymmetric: a kind here but not there renders a permanently blank cell where a
 * value is expected, and a kind there but not here opens a real subscription
 * against the customer's source for a value nothing displays. Change both.
 *
 * An explicit allowlist rather than "anything that is not an object": "object" is
 * also a legitimate plugin-published object node, so the negative test is
 * ambiguous. This way adding an archetype is a deliberate one-line decision.
 */
export const VALUE_BEARING_KINDS: ReadonlySet<string> = new Set(['topic']);

/**
 * The grid's logical type for a row.
 *
 * `mapScalar` handles the leaf case; this adds the parent case. A parent whose
 * kind is value-bearing keeps its real mapped type (a broker topic stays "json"),
 * and every other parent becomes the structural "object"/"array" it has always
 * been. Arrays are detected the way the caller always did — from the vendor type
 * naming one — so a genuinely indexed container still reads as an array.
 */
export function logicalType(dataType: string, childCount: number, kind: string, mapScalar: (dt: string) => string): string {
  if (childCount <= 0) return mapScalar(dataType);
  if (VALUE_BEARING_KINDS.has(kind)) return mapScalar(dataType);
  return /array|\[/.test(dataType) ? 'array' : 'object';
}

/**
 * Whether a row's logical type means it holds a value to show, age and edit.
 *
 * Keyed on the TYPE rather than on the child count, which is the whole point of
 * this module: after `logicalType`, "object"/"array" means structural and
 * anything else means a value. A caller that still has only a child count is
 * asking the old question.
 */
export function carriesValue(type: string): boolean {
  return type !== 'object' && type !== 'array';
}

/**
 * How much of a raw message the value cell will show.
 *
 * Deliberately NOT `PAYLOAD_MAX_CHARS` (20 000) from `base-ui/src/payload.ts`:
 * that constant is sized for a dialog and says so. This text is written into
 * `textContent` AND `title` on every pooled row, so at 20 000 a single screen of
 * broker rows would carry megabytes of DOM string and re-write them on every
 * scroll frame. The full message belongs in the value editor, which is where
 * `readPayload`'s truncation reporting is meaningful.
 *
 * 200 shows a typical sensor message whole while keeping the column bounded.
 */
export const CELL_MAX_CHARS = 200;

/** Clip a value-cell string to `CELL_MAX_CHARS`, marking that it was cut. */
export function clipCell(s: string): string {
  return s.length > CELL_MAX_CHARS ? s.slice(0, CELL_MAX_CHARS - 1) + '…' : s;
}
